import * as React from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MdOutlineEmail } from "react-icons/md";
import { FORM_ASYNC_API_URL } from "../utils/constants";

const BORDER = `1.2px solid ${"#0F4F96"}`;

const STATUS_COLOR = {
  completed: "success",
  inprogress: "warning",
  failed: "error",
};

// Every dump request of the selected course (the table above shows only the
// latest), so email ids can be given access to any completed one.
export default function CandidateDumpHistory({ courses = [], onAddEmail, refreshKey = 0 }) {
  const [courseId, setCourseId] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadRequests = React.useCallback(async (selectedCourseId) => {
    if (!selectedCourseId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${FORM_ASYNC_API_URL}/dumpRequests/${selectedCourseId}`, {
        headers: { Authorization: `Bearer ${Cookies.get("mhet_mst_token")}` },
      });
      if (data?.statusCode === 200) {
        setRows(data?.result || []);
      } else {
        setRows([]);
        setError(data?.message || "Failed to load dump requests");
      }
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load dump requests");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadRequests(courseId);
  }, [courseId, refreshKey, loadRequests]);

  const columns = ["Request #", "Type", "Status", "File", "Email ID Access", "Action"];

  return (
    <Box sx={{ mt: 2, mb: 2, pr: 2 }}>
      <Box sx={{ width: "100%", backgroundColor: "#e8e8e8", p: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <p style={{ fontWeight: "500", color: "#212121", fontSize: 18, lineHeight: "100%", marginLeft: 5 }}>
          Dump history
        </p>
        <FormControl size="small" sx={{ minWidth: 280, backgroundColor: "#fff" }}>
          <InputLabel id="dump-history-course-label">Select course</InputLabel>
          <Select
            labelId="dump-history-course-label"
            label="Select course"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
          >
            {courses.map((course) => (
              <MenuItem key={course?.courseid} value={course?.courseid}>
                {course?.coursename}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ mt: 2, background: "none", border: `1px solid ${"#0F4F96"}`, boxShadow: "none" }}
      >
        <Table size="small" sx={{ minWidth: 650, borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              {columns.map((name, i) => (
                <TableCell
                  key={name}
                  sx={{ color: "#0F4F96", textAlign: "center", borderRight: i !== columns.length - 1 ? BORDER : "none" }}
                >
                  {name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!courseId || loading || error || !rows.length ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: "center", color: error ? "#d32f2f" : "#0F4F96" }}>
                  {!courseId
                    ? "Select a course to see all its dump requests"
                    : loading
                      ? "Loading..."
                      : error || "No dump generated for this course yet"}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const cellSx = { textAlign: "center", borderRight: BORDER };
                return (
                  <TableRow key={row.requestid}>
                    <TableCell sx={cellSx}>{row.requestid}</TableCell>
                    <TableCell sx={cellSx}>{row.type || "-"}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        size="small"
                        label={row.status || "-"}
                        color={STATUS_COLOR[row.status] || "default"}
                        variant="outlined"
                      />
                      {row.errormessage && (
                        <Box sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5 }}>{row.errormessage}</Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontSize: 13, wordBreak: "break-all" }}>{row.fileName || "-"}</TableCell>
                    <TableCell sx={{ ...cellSx, fontSize: 13, wordBreak: "break-all", textAlign: "left" }}>
                      {row.emailaccess?.length
                        ? row.emailaccess.map((email) => <div key={email}>{email}</div>)
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {row.status === "completed" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MdOutlineEmail />}
                          onClick={() => onAddEmail?.(courseId, row.requestid)}
                          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                        >
                          Add email id
                        </Button>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
